import { getModes, getAltMode, patchAltMode, patchModeParam, getDiagnostics } from '../../api/altmode.api'

const state = {
  modes: null,
  altMode: null,
  diagnostics: null,
}

const getters = {
  getModes(state) {
    return state.modes
  },
  getAltMode(state) {
    return state.altMode
  },
  getDiagnostics(state) {
    return state.diagnostics
  },
}

const actions = {
  async fetchModes({ commit }, device_id) {
    try {
      const response = await getModes(device_id)
      commit('SET_MODES', response.data)
    } catch (error) {
      console.error('[altmode] fetchModes', error)
    }
  },
  async fetchAltMode({ commit }, device_id) {
    try {
      const response = await getAltMode(device_id)
      commit('SET_ALT_MODE', response.data)
    } catch (error) {
      console.error('[altmode] fetchAltMode', error)
    }
  },
  /** Load the device's diagnostics (LTE/WiFi/GPS connectivity reports) into the store. */
  async fetchDiagnostics({ commit }, device_id) {
    try {
      const response = await getDiagnostics(device_id)
      commit('SET_DIAGNOSTICS', response.data)
    } catch (error) {
      console.error('[altmode] fetchDiagnostics', error)
    }
  },
  /** Drop the cached alt_mode. Call before a fresh load so a failed fetch can't leave a stale value. */
  clearAltMode({ commit }) {
    commit('SET_ALT_MODE', null)
  },
  /**
   * Resolve the tracker matching `serial` and load its alt_mode into the store.
   * Shared by the mode About / Config pages and the theft file. Device lookup is
   * just plumbing to reach /alt_mode/ (keyed by device id), so this lives here.
   * @returns the matched device, or null.
   */
  async loadAltModeForSerial({ dispatch, rootGetters }, { serial, token }) {
    dispatch('clearAltMode') // start clean — a failed fetch must not surface a prior device's mode
    if (token) dispatch('users/setToken', token, { root: true })
    try {
      await dispatch('devices/fetchDevices', 'tracker', { root: true })
    } catch {
      // Callers (webview-hosted pages) treat a failed fetch like an unknown serial.
      return null
    }
    const device = rootGetters['devices/getDevices'].filter((d) => d.serial == serial)[0] || null
    if (device) await dispatch('fetchAltMode', device.id) // local — same module
    return device
  },
  /**
   * Activate an alternate mode: resolve the mode entry from /modes/ by backend
   * name, then PATCH mode_param with its value + preferred tlv_data.
   * Returns the mode_param response data (callers read `requested_mode`), or
   * null on failure.
   */
  async activateAltMode({ state, dispatch }, { device_id, backendName }) {
    try {
      if (!state.modes) await dispatch('fetchModes', device_id)
      const entry = state.modes?.modes?.find((m) => m.name === backendName)
      if (!entry) {
        console.warn(`[altmode] no mode named ${backendName} in /modes/ response`)
        return null
      }
      const response = await patchModeParam(device_id, {
        mode: entry.value,
        name: backendName,
        alt: true,
        tlv_data: entry.params?.preferred?.tlv_data,
      })
      return response.data
    } catch (error) {
      console.error('[altmode] activateAltMode', error)
      return null
    }
  },
  /** Exit the active alternate mode. Returns the mode_param response data (restore mode), or null on failure. */
  async deactivateAltMode(context, device_id) {
    try {
      const response = await patchModeParam(device_id, { name: null })
      return response.data
    } catch (error) {
      console.error('[altmode] deactivateAltMode', error)
      return null
    }
  },
  /** Adjust an active alt mode's config (expire_date, params). Returns response data or null. */
  async patchAltModeConfig({ commit }, { device_id, body }) {
    try {
      const response = await patchAltMode(device_id, body)
      commit('SET_ALT_MODE', response.data)
      return response.data
    } catch (error) {
      console.error('[altmode] patchAltModeConfig', error)
      return null
    }
  },
}

const mutations = {
  SET_MODES(state, data) {
    state.modes = data
  },
  SET_ALT_MODE(state, data) {
    state.altMode = data
  },
  SET_DIAGNOSTICS(state, data) {
    state.diagnostics = data
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
